import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, CheckCircle, LinkBreak, ArrowSquareOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { clearCompanyCache } from '@/lib/company';

export function RazorpaySettingsCard() {
  const [state, setState] = useState(null);
  const [keyId, setKeyId] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = () => api.get('/settings/razorpay').then(r => { setState(r.data); setKeyId(r.data.key_id || ''); }).catch(() => setState({ configured: false }));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put('/settings/razorpay', { key_id: keyId, key_secret: secret });
      setState(data); setSecret(''); setEditing(false); clearCompanyCache();
      toast.success('Razorpay connected — customers can now pay your invoices online');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Could not connect Razorpay');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Razorpay? Existing payment links will stop working.')) return;
    setBusy(true);
    try {
      await api.delete('/settings/razorpay');
      setState({ configured: false, key_id: '' }); setKeyId(''); setSecret(''); clearCompanyCache();
      toast.success('Razorpay disconnected');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Could not disconnect');
    } finally { setBusy(false); }
  };

  if (!state) return null;
  const connected = state.configured && !editing;

  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none transition-shadow duration-200 hover:shadow-sm" data-testid="razorpay-settings-card">
      <CardContent className="p-0">
        <div className="flex items-start justify-between gap-3 flex-wrap px-6 py-4 border-b border-[#E4E4E7]">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-sm bg-[#0A0A0A] text-white shrink-0"><CreditCard size={16} weight="bold" /></span>
            <div>
              <h2 className="text-base font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Online Payments (Razorpay)</h2>
              <p className="text-xs text-[#52525B] mt-0.5">Let customers pay invoice dues online. Money settles directly into <b>your</b> Razorpay account.</p>
            </div>
          </div>
          {state.configured ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="razorpay-status-connected">
              <CheckCircle size={14} weight="fill" /> Connected · {state.mode === 'live' ? 'Live' : 'Test'} mode
            </span>
          ) : (
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1" data-testid="razorpay-status-notconnected">Not connected</span>
          )}
        </div>

        <div className="px-6 py-5">
        {connected ? (
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#52525B]">
            <span>Key ID: <code className="font-mono text-[#0A0A0A]" data-testid="razorpay-key-id-display">{state.key_id}</code></span>
            <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => setEditing(true)} data-testid="razorpay-change-btn">Change keys</Button>
            <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={disconnect} disabled={busy} data-testid="razorpay-disconnect-btn">
              <LinkBreak size={13} className="mr-1" /> Disconnect
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Key ID</Label>
                <Input value={keyId} onChange={e => setKeyId(e.target.value)} placeholder="rzp_live_xxxxxxxxxxxx" className="rounded-sm mt-1.5 font-mono bg-white border-[#E4E4E7] focus-visible:ring-0 focus-visible:border-[#0A0A0A]" data-testid="razorpay-key-id-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Key Secret</Label>
                <Input type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="Paste the secret shown once by Razorpay" className="rounded-sm mt-1.5 font-mono bg-white border-[#E4E4E7] focus-visible:ring-0 focus-visible:border-[#0A0A0A]" autoComplete="off" data-testid="razorpay-key-secret-input" />
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button type="button" onClick={save} disabled={busy || !keyId.trim() || !secret.trim()} className="h-9 rounded-sm bg-[#0A0A0A] text-white hover:bg-black/80 hover:text-white text-xs font-semibold" data-testid="razorpay-connect-btn">
                {busy ? 'Verifying with Razorpay…' : 'Verify & Connect'}
              </Button>
              {editing && <Button type="button" variant="ghost" className="h-9 text-xs" onClick={() => { setEditing(false); setSecret(''); setKeyId(state.key_id || ''); }} data-testid="razorpay-cancel-btn">Cancel</Button>}
              <a href="https://dashboard.razorpay.com/app/website-app-settings/api-keys" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline" data-testid="razorpay-help-link">
                Where do I get these? <ArrowSquareOut size={12} />
              </a>
            </div>
            <p className="text-[11px] text-[#A1A1AA]">Razorpay Dashboard → Account &amp; Settings → API Keys → Generate Key. The secret is stored securely and never shown again.</p>
          </div>
        )}
        </div>
      </CardContent>
    </Card>
  );
}
